import React, {
    useContext, useEffect,
} from 'react'

import {
    channelWrapper, embedChannelWrapper, heroWrapper,
} from './styles'
import {
    useAppDispatch,
    useAppSelector, useAppSelectorWithShallowEquality,
} from 'hooks'

import Channel from 'components/Channel'
import {EmbedContext} from 'components/EmbedContext'
import {EmbedEvent} from 'embed/types/events'
import {EmbedHero} from './components/EmbedHero/EmbedHero'
import {JitsiState} from 'reducers/jitsi'

import {config} from 'embed/listeners'
import {describeChannel} from 'api/action/describeChannel'
import {getChannelMembers} from 'selectors/ninchat'
import {isVideoChannel as isVideoChannelCheck} from 'utils/channels'
import {styled} from 'stitches/config'

import {useSendToParent} from 'embed/utils/event'
import {useTranslation} from 'react-i18next'

const EmbedChannelWrapper = styled('div', embedChannelWrapper)
const ChannelWrapper = styled('div', channelWrapper)
const HeroWrapper = styled('div', heroWrapper)

interface EmbedChannelProps {
  channelId: string
  resetEmbed: (clearData?: boolean) => void
}

export const EmbedChannel = (props: EmbedChannelProps) => {
    const {
        channelId, resetEmbed,
    } = props
    const targetOrigin = config.get('targetOrigin') || ''
    const sendToParent = useSendToParent()
    const userId = useAppSelector(({ninchat}) => ninchat.user_id)
    const channelMembers = useAppSelector((state) => getChannelMembers(state, channelId))

    const embed = useContext(EmbedContext)
    const dispatch = useAppDispatch()
    const currentChannel = useAppSelector((state) => state.ninchat.user_channels?.[channelId] || {})

    const heroFullVisible = embed?.embedState?.heroFullVisible
    const heroFullAbsoluteVisible = embed?.embedState?.heroFullAbsoluteVisible
    const isVideoChannel = isVideoChannelCheck(currentChannel)

    const {t} = useTranslation()

    const {
        activeVideoChannelId,
        isChatOpen,
    } = useAppSelectorWithShallowEquality(({jitsi}: {jitsi: JitsiState}) => ({
        activeVideoChannelId: jitsi.activeVideoChannelId,
        isChatOpen: jitsi.isChatOpen ?? false,
        isJitsiActive: jitsi?.isActive ?? false,
    }))

    const selectedMembers = channelMembers.filter((member) => {
        // if there is 2 members only we are excluding the current user
        // eslint-disable-next-line no-extra-parens
        if ((member.id === userId && channelMembers.length <= 2) || !member.details) {
            return false
        }
        return true
    })


    useEffect(() => {
        embed?.setEmbedState((prevState) => {
            if (prevState.channelMembersAll === channelMembers) {
                return prevState
            }

            const allChannelMembers = prevState.channelMembersAll || []
            channelMembers.forEach((member) => {
                const existingMember = allChannelMembers.find((m) => m.id === member.id)
                if (!existingMember) {
                    allChannelMembers.push(member)
                }
            })

            return {
                ...prevState,
                channelMembersAll: allChannelMembers,
            }
        })
    }, [channelMembers])

    useEffect(() => {
        if (channelId) {
            dispatch(describeChannel({channel_id: channelId})).unwrap()
                .then(() => {
                    // Description fetched successfully
                })
                .catch((error) => {
                    let title = t('Error describing channel')

                    if (error.error_type === 'permission_denied') {
                        title = t('Session expired')
                    }

                    embed?.setEmbedState((prevState) => ({
                        ...prevState,
                        error: {
                            message: t('We encountered an error. Please close and restart chat to continue.'),
                            title,
                        },
                    }))
                })
        }
    }, [channelId])

    useEffect(() => {
        if (currentChannel.channel_attrs) {
            if (selectedMembers.length) {
                sendToParent({
                    channel_attrs: currentChannel.channel_attrs,
                    event: EmbedEvent.EmbedMembers,
                    members: selectedMembers,
                    targetOrigin,
                })
                embed?.setEmbedState((prevState) => ({
                    ...prevState,
                    channelMembers: selectedMembers,
                    lastChannelId: channelId,
                }))
            } else if (embed?.embedState?.channelMembers && embed?.embedState?.channelMembers?.length > 0) {
                sendToParent({
                    channel_attrs: currentChannel.channel_attrs,
                    event: EmbedEvent.EmbedMembers,
                    members: embed?.embedState?.channelMembers || [],
                    targetOrigin,
                })
                embed?.setEmbedState((prevState) => ({
                    ...prevState,
                    channelMembers: embed?.embedState?.channelMembers,
                    lastChannelId: channelId,
                }))
            }
        }
    }, [currentChannel.channel_attrs, JSON.stringify(selectedMembers)])

    useEffect(() => {
        sendToParent({
            channel_status: currentChannel.channel_status,
            event: EmbedEvent.EmbedChannelStatus,
            targetOrigin,
        })
    }, [currentChannel.channel_status])

    return (
        <EmbedChannelWrapper>
            <HeroWrapper heroFullAbsoluteVisible={heroFullAbsoluteVisible} heroFullVisible={heroFullVisible} isVideoChannel={isVideoChannel}>
                <EmbedHero channelId={channelId} members={embed?.embedState?.channelMembers} resetEmbed={resetEmbed} type='chat' />
            </HeroWrapper>
            {currentChannel.channel_attrs ?
                <ChannelWrapper activeVideoChannelId={!!activeVideoChannelId} isChatOpen={isChatOpen}>
                    <Channel params={{channel_id: channelId}} />
                </ChannelWrapper>
                : null}
        </EmbedChannelWrapper>
    )
}

